import React, {useEffect, useReducer, useState} from 'react'
import {useLocation, useParams} from 'react-router-dom'
import {useMutation} from 'react-query'
import 'react-datepicker/dist/react-datepicker.css'
import * as dayjs from 'dayjs'
import * as utc from 'dayjs/plugin/utc'
import * as timezone from 'dayjs/plugin/timezone'
import * as relativeTime from 'dayjs/plugin/relativeTime'
import {useSolv, useTenant} from "./components/SolvProvider"
import MainMenu from "./MainMenu.js"
import {ActionButton, DropdownMenu, FormBody, FormHeader, MainContainer, useAlert} from "./components/FormComps.js"
import {uploadImage} from "./components/ImageUtils.js"
import {
  clearAllErrors,
  showError,
  validateDomainName,
  validateField,
  validateUrl
} from "./components/ValidationUtils.js"
import {useIntlEx} from "./components/IntlUtils.js"
import {
  AccountHeader,
  AccountTabs,
  mkHref,
  useClientSupportLevelOpts,
  useResellerType,
  useTaxType
} from "./components/AccountUtils";
import {pathReducer} from "./components/ReactUtils";
import {openDialogCurried} from "./components/DialogUtils.js"
import getLogger from "./components/Logging.js"
import AccountDeleteDialog from "./AccountDeleteDialog"
import ResellerInviteDialog from "./ResellerInviteDialog"
import ClientInviteDialog from "./ClientInviteDialog";
import CreditTransactionTopupDialog from "./CreditTransactionTopupDialog"
import ResellerMoveDialog from "./ResellerMoveDialog";
import {canExtendCredits, CreditsExpiring} from "./components/CreditTransactionUtils";
import CreditTransactionAdjustDialog from "./CreditTransactionAdjustDialog";
import {deepSet} from "./components/Utils";
import {
  AccountClientSettingsComps,
  AccountContactAndBillingComps,
  AccountCoreComps,
  AccountResellerComps,
  AccountSystemSettingsComps
} from "./AccountEditComps";
import "./App.css"

const log = getLogger("AccountEdit")

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(relativeTime)

const DefaultCPMs = {"SOLV": 5.0, "IMAGE": 5.0, "VIDEO": 10.0}

export default function AccountEdit(props) {

  const {env, api, session, setBusy} = useSolv()
  const {intl} = useIntlEx()

  const location = useLocation()
  const params = useParams()

  const {tenant} = useTenant(params.tenantId)

  const [alert, setAlert] = useAlert()

  const [editMode, setEditMode] = useState(location.pathname.endsWith("_edit") ? "EDIT" : null)
  const [touched, setTouched] = useState(null)

  const {getResellerType, getChangeableResellerTypeOpts, findResellerTypeOpt} = useResellerType()
  const {taxTypeOpts, findTaxTypeOpt} = useTaxType()
  const {clientAccessLevelOpts, findClientSupportLevelOpt, findClientSupportLevelOptByLevel} = useClientSupportLevelOpts()

  const [industry, setIndustry] = useState(null)

  const [modifyTenant, updateModifyTenant] = useReducer(pathReducer, {})
  const [clickUrls, setClickUrls] = useState(null)
  const [restrictDomains, setRestrictDomains] = useState(null)
  const [allowedDomains, setAllowedDomains] = useState(null)
  const [disallowedDomains, setDisallowedDomains] = useState(null)
  const [useContactInfo, setUseContactInfo] = useState(null)
  const [overrideAdSystemProps, setOverrideAdSystemProps] = useState(null)
  const [resellerType, setResellerType] = useState(null)
  const [commissionRates, setCommissionRates] = useState(null)
  const [cpms, setCpms] = useState(DefaultCPMs)
  const [maxLevels, setMaxLevels] = useState(null)
  const [taxType, setTaxType] = useState(null)
  const [flags, setFlags] = useState(null)
  const [amobeeBehaviorSegmentSet, setAmobeeBehaviorSegmentSet] = useState(null)
  const [clientAccessLevel, setClientAccessLevel] = useState(false)

  const [changeableResellerTypeOpts, setChangeableResellerTypeOpts] = useState(null)

  useEffect(() => {
    if (tenant) {
      // // log.debug("useEffect: tenant=", tenant)
      if ("ONBOARDING" === tenant.status) {
        setAlert({"type": "notice", "text": intl.msg("account_onboarding_notice")})
        setEditMode("EDIT")
      }
      updateModifyTenant({path: ".", value: {...tenant, ownerEmailAddress: tenant?.owner?.emailAddress}})
      setClickUrls(tenant.clickUrls)
      const _allowedDomains = tenant.allowedDomains?.length > 0 ? tenant.allowedDomains.map(d => {
        return {label: d.trim(), value: d.trim()}
      }) : null
      setAllowedDomains(_allowedDomains)
      const _disallowedDomains = tenant.disallowedDomains?.length > 0 ? tenant.disallowedDomains.map(d => {
        return {label: d.trim(), value: d.trim()}
      }) : null
      setDisallowedDomains(_disallowedDomains)
      setRestrictDomains(_allowedDomains?.length > 0 || _disallowedDomains?.length > 0)
      setChangeableResellerTypeOpts(getChangeableResellerTypeOpts(tenant))
      setResellerType(findResellerTypeOpt(getResellerType(tenant)))
      setTaxType(findTaxTypeOpt(tenant.taxType))
      setCommissionRates(tenant.commissionRates)
      setMaxLevels(tenant.maxDepth + 1)
      setIndustry(tenant.industryId)
      setClientAccessLevel(findClientSupportLevelOptByLevel(tenant.supportLevel))
      setFlags(tenant.flags?.map(d => {
        return {label: d.trim(), value: d.trim()}
      }))
      setUseContactInfo(["null", "undefined"].includes(typeof(tenant.billingInfo?.name)))
      setOverrideAdSystemProps(!!tenant.props)
      if (tenant.props && tenant.props.adSystem && tenant.props.adSystem.AMOBEE) {
        if (tenant.props.adSystem.AMOBEE.cpms) {
          setCpms(tenant.props.adSystem.AMOBEE.cpms)
        }
        if (tenant.props.adSystem.AMOBEE.behavioralSegmentSetId) {
          api.getAmobeeBehaviorSegmentSet(tenant.props.adSystem.AMOBEE.behavioralSegmentSetId)
            .then(({data}) => {
              // log.debug("getAmobeeBehaviorSegmentSet: ", data)
              setAmobeeBehaviorSegmentSet({
                value: data.behaviorSegmentSetId,
                label: data.behaviorSegmentSetName,
                popDampFactor: data.popDampFactor,
              })
            })
            .catch((error) => {
              // log.debug("Error", error)
            })
        }
      }

      if (tenant.industryId) {
        api.getIndustry(tenant.industryId)
          .then(({data}) => {
            // log.debug("getIndustry: data=", data)
            setIndustry({
              value: data.industryId,
              label: data.industryName._,
              description: data.industryDescription._,
            })
          })
          .catch((error) => {
            log.error("getIndustry: error=", error)
          })
      }

      if (CreditsExpiring(tenant.credits)) {
        setAlert({"type": "warning", "text": intl.msg("credits_alert_expiring", {accessType: tenant.accessInfo?.accessType, canExtend: canExtendCredits(tenant, session.user).toString(), expireDays: intl.fmDaysFrom(tenant.credits.expiresOn), a: chunk => <a className="text-link" href="#" onClick={openDialogCurried("dlg_credits_extend_expiry")}>{chunk}</a>})})
      }

    }
  }, [tenant])


  function setLogoUrl(v) {
    updateModifyTenant({path: "logoUrl", value: v})
    setTouched(true)
  }

  useEffect(() => {
    log.debug(">>>Z: modifyTenant=", modifyTenant)
  }, [modifyTenant])

  function handleFieldChange(v) {
    log.debug(">>>Z: handleFieldChange: v=", v)
    if (v) {
      if (v.alert) {
        setAlert(v.alert)
      }
      else if (v.path) {
        updateModifyTenant(v)
        setTouched(true)
      }
      else if (v.touched) {
        setTouched(v.touched)
      }
    }
  }

  function validate() {

    let opt = {focusEl: null, valid: true}

    clearAllErrors("frmMain")

    validateField("inp_tenantName", modifyTenant.tenantName && modifyTenant.tenantName.trim().length > 0, "required", opt)

    if ("CLIENT" === modifyTenant.tenantTypeId && modifyTenant.homePage) {
      validateField("inp_homePage", validateUrl(modifyTenant.homePage), "valid", opt)
    }

    if (clickUrls?.length > 0) {
      for (let i = 0; i < clickUrls.length; i++) {
        const clickUrl = clickUrls[i]
        if (!clickUrl || !clickUrl.id || !clickUrl.name || clickUrl.name.trim().length === 0 || !clickUrl.url || clickUrl.url.trim().length === 0 || !validateUrl(clickUrl.url)) {
          validateField(`#inp_clickUrls_name_${i}`, false, "valid", opt)
          break
        }
      }
    }

    if (allowedDomains?.length > 0) {
      validateField("inp_allowedDomains",  allowedDomains.map(v => v.value ? v.value.trim() : "").every(v => validateDomainName(v)), "valid", opt)
    }
    if (disallowedDomains?.length > 0) {
      validateField("inp_disallowedDomains",  disallowedDomains.map(v => v.value ? v.value.trim() : "").every(v => validateDomainName(v)), "valid", opt)
    }

    if (tenant.isReseller() && session.user.isSystem()) {

      if (!["DEMO", "HOUSE", "TEST"].includes(resellerType?.value)) {

        if (["MASTER"].includes(resellerType?.value)) {
          validateField("inp_maxLevels", parseInt(maxLevels) > 1 && parseInt(maxLevels) <= 4, "valid", opt)
        }

        if (!commissionRates || commissionRates.length === 0) {
          validateField("inp_commissions", false, "required", opt)
        }
        else {
          if (["STANDALONE", "SUB"].includes(resellerType?.value)) {

            const cs = parseFloat(commissionRates[0])
            validateField("inp_commission", !isNaN(cs) && cs > 0.00 && cs <= 20.00, "valid", opt)

          } else {

            if (maxLevels > commissionRates.length) {
              validateField("inp_commission_0", false, "valid", opt)
            }

            const cs = commissionRates.slice(0, maxLevels)

            if (cs.filter(c => isNaN(parseFloat(c))).length > 0) {
              validateField("inp_commission_0", false, "valid", opt)
            }
            else {
              const ttl = cs.map(c => parseFloat(c)).reduce((acc, curr) => acc + curr)
              // log.debug("validate: ttl=", ttl)
              if (isNaN(ttl) || ttl <= 0.00 || ttl > 20.00) {
                validateField("inp_commission_0", false, "valid", opt)
              }
            }
          }
        }
      }
    }

    if (opt.focusEl) {
      document.getElementById(opt.focusEl).focus()
    }

    // log.debug("validate: opt=", opt)

    return opt.valid
  }

  const saveMutation = useMutation(payload => api.updateTenant(modifyTenant.tenantId, payload))

  async function handleEditClick() {
    // const ctls = document.getElementsByClassName("form-control")
    // if (ctls && ctls.length > 0 && ctls[0]) {
    //   // log.debug("handleEditClick: frm=", ctls[0])
    //   ctls[0].focus()
    // }
    // setEditMode("EDIT")
    window.location = mkHref({suffix: "/_edit", user: session.user, tenant: tenant})
  }

  async function handleSaveClick() {

    setBusy(intl.msg("saving"))

    try {

      if (validate()) {

        let finalLogoUrl = modifyTenant.logoUrl

        if (finalLogoUrl && finalLogoUrl.startsWith("data:")) {
          finalLogoUrl = await uploadImage({
            api: api,
            env: env,
            imageData: finalLogoUrl,
            imagePath: `t/${modifyTenant.tenantId}/logo`,
            setImageUrl: setLogoUrl,
          })
        }

        // log.debug(`handleSaveClick: `, modifyTenant.allowedDomains)

        const payload = {
          brandId: modifyTenant.brandId,
          tenantName: modifyTenant.tenantName,
          displayName: modifyTenant.displayName,
          homePage: modifyTenant.homePage,
          contactInfo: modifyTenant.contactInfo,
          billingInfo: modifyTenant.billingInfo,
          logoUrl: finalLogoUrl,
          clickUrls: null,
          allowedDomains: modifyTenant?.allowedDomains?.length > 0 ? modifyTenant.allowedDomains.map(d => d.trim()) : [],
          disallowedDomains: modifyTenant?.disallowedDomains?.length > 0 ? modifyTenant.disallowedDomains.map(d => d.trim()) : [],
        }

        if (tenant.isReseller()) {
          const resellerType = modifyTenant.resellerType || getResellerType(modifyTenant)
          payload.resellerType = resellerType
          if (["DEMO", "HOUSE", "TEST"].includes(payload.resellerType)) {
            payload.commissionRates = []
          }
          else if (["STANDALONE", "SUB"].includes(payload.resellerType)) {
            payload.commissionRates = modifyTenant.commissionRates.slice(0, 1)
          }
          else {
            payload.commissionRates = modifyTenant.commissionRates
          }
          payload.maxDepth = modifyTenant.maxDepth
          payload.industryId = industry.value
          payload.taxRate = modifyTenant.taxRate
          payload.taxType = taxType?.value
          payload.supportLevel = modifyTenant.supportLevel || 0
        }
        else if (tenant.isClient()) {
          payload.industryId = industry.value
          payload.maxDepth = 0
        }
        else {
          payload.maxDepth = 0
        }

        if (isInvitation()) {
          payload.ownerEmailAddress = modifyTenant.ownerEmailAddress
        }

        if (session.user?.isSystem()) {

          payload.props = {}

          if (modifyTenant.props?.adSystem?.AMOBEE?.cpms) {
            // log.debug(">>>Z: handleSaveClick: 1 cpms=", modifyTenant.props?.adSystem?.AMOBEE?.cpms)
            let cpms = modifyTenant.props.adSystem.AMOBEE.cpms
            cpms = Object.fromEntries(
              Object.entries(cpms).map(([key, value]) => {
                if (value === null) {
                  return [key, null]
                }
                if (typeof value === "string") {
                  if (value.trim() === "") {
                    return [key, null]
                  }
                  const num = parseFloat(value);
                  return [key, isNaN(num) ? null : num]
                }
                if (typeof value === "number") {
                  return [key, value]
                }
                return [key, null]
              })
            )
            // log.debug(">>>Z: handleSaveClick: 2 cpms=", cpms)
            payload.props = {
              adSystem: {
                AMOBEE: {
                  cpms: cpms
                }
              }
            }
          }

          payload.flags = modifyTenant.flags

          if (modifyTenant.props?.adSystem?.AMOBEE?.behavioralSegmentSetId) {
            deepSet(payload.props, "adSystem.AMOBEE.behavioralSegmentSetId", modifyTenant.props?.adSystem?.AMOBEE?.behavioralSegmentSetId)
          }

        }

        log.debug("handleSaveClick: Saving: payload=", payload)

        const res = await saveMutation.mutateAsync(payload)

        window.location = mkHref({suffix: "/", user: session.user, tenant: tenant})

      }
      else {
        // log.debug("Validation error")
        setAlert({type: "error", text: intl.msg("error_validation")})
        setTouched(true)
      }
    }
    catch (ex) {
      log.debug("Save tenant failed: ex=", ex)
      handleError(ex)
    }
    finally {
      setBusy(null)
    }
  }

  function handleCancelClick() {
    window.location = mkHref({suffix: "/", user: session.user, tenant: tenant})
  }

  function getErrorMsg(error) {
    let res = null
    if (error && error.code) {
      // log.debug("getErrorMsg: error=", error)
      const code = error.code
      res = {
        code: code
      }
      switch (code) {
        case "DUPLICATE_KEY":
        case "DUPLICATE_TENANT":
          res.msg = intl.msg("error_validation", {tenantName: error.tenantName})
          res.field = "inp_tenantName"
          res.constraint = "valid"
          break
        default:
          res.msg = intl.msg("error_failed", {emailAddress: error.emailAddress})
          break
      }
    }
    else {
      return intl.msg("error_failed")
    }
    return res
  }

  function handleError(error) {
    const msg = getErrorMsg(error)
    if (msg) {
      setAlert({type: "error", text: msg.msg})
      if (msg.field) {
        showError(msg.field, msg.constraint)
      }
    }
    else {
      setAlert({type: "error", text: intl.msg("error_failed")})
    }
  }

  function isEditing() {
    return ["ADD", "EDIT"].includes(editMode)
  }

  function isInvitation() {
    return (["INVITING", "ONBOARDING"].includes(tenant.status))
  }

  function isAdding() {
    return ("ADD" === editMode)
  }

  function isAffiliateOrReseller() {
    return ["AFFILIATE", "RESELLER"].includes(tenant.tenantTypeId)
  }

  function MoreMenu(props) {
    const comps = []
    return (
      <>
        {
          comps.length > 0 &&
            <DropdownMenu>
              {comps}
            </DropdownMenu>
        }
      </>
    )
  }

  useEffect(() => {
    // log.debug(">>>Z: modifyTenant=", modifyTenant)
  }, [modifyTenant])

  return (
    tenant &&
      <>

        <MainContainer tenant={tenant} menu={MainMenu}>

          <FormHeader>
            <AccountTabs tenant={tenant}/>
            <FormHeader.Toolbar>
              <FormHeader.Toolbar.Title>
                <AccountHeader tenant={tenant}/>
              </FormHeader.Toolbar.Title>
              <FormHeader.Toolbar.Controls>
                {
                  "ONBOARDING" === tenant.status ? (
                    <>
                      <ActionButton action="save" flavor="primary" onClick={handleSaveClick}/>
                    </>
                  ) : (
                    <>
                      <ActionButton action="save" flavor="primary" onClick={handleSaveClick}/>
                      <ActionButton action="cancel" onClick={handleCancelClick}/>
                    </>
                  )
                }
              </FormHeader.Toolbar.Controls>
            </FormHeader.Toolbar>
            <FormHeader.Alert alert={alert}/>
          </FormHeader>

          <FormBody>

            <AccountCoreComps
              data={modifyTenant}
              update={handleFieldChange}
              hidden={{
                sec_account: !tenant.accessingAs("ADMINISTRATOR/INSTREAM,DOWNSTREAM/RESELLER,SYSTEM"),
                inp_ownerEmailAddress: !(isAdding() || isInvitation()),
              }}
            />

            <AccountResellerComps
              data={modifyTenant}
              prev={tenant}
              update={handleFieldChange}
              hidden={{
                sec_reseller: !tenant.isReseller()
              }}
            />

            <AccountContactAndBillingComps
              data={modifyTenant}
              update={handleFieldChange}
              // hidden={{
              //   sec_contact: tenant.isClient() ? (isAdding() || isInvitation()) : (!(tenant.isAffiliate() || tenant.isReseller())),
              //   sec_billing: !(tenant.isAffiliate() || tenant.isReseller()),
              // }}
              required={{
                inp_displayName: isAffiliateOrReseller(),
                inp_contactInfo: isAffiliateOrReseller(),
                inp_logoUrl: isAffiliateOrReseller(),
                inp_homePage: isAffiliateOrReseller(),
                inp_billingName: (tenant.isAffiliate() || tenant.isReseller()),
                inp_billingAddress: (tenant.isAffiliate() || tenant.isReseller()),
              }}
              disabled={tenant.isSystem()}
              collapsed={tenant.isClient()}
            />

            <AccountClientSettingsComps
              data={modifyTenant}
              update={handleFieldChange}
              hidden={{
                sec_client_misc: !tenant.isClient()
              }}
            />


            <AccountSystemSettingsComps
              data={modifyTenant}
              placeholder={tenant}
              update={handleFieldChange}
              hidden={{
                sec_system: !tenant.accessingAs("MANAGER/*/SYSTEM"),
                inp_flags: tenant.isSystem(),
            }}/>

          </FormBody>

        </MainContainer>

        <CreditTransactionTopupDialog key={"topup"} tenant={tenant}/>

        <CreditTransactionAdjustDialog key={"extend"} tenant={tenant} currencyCode={tenant.currencyCode}/>

        <ResellerInviteDialog tenant={tenant} reinvite={tenant && "INVITING" === tenant.status}/>

        <ResellerMoveDialog tenant={tenant}/>

        <ClientInviteDialog tenant={tenant}/>

        <AccountDeleteDialog tenant={tenant}/>

      </>
  )
}